import { clsx } from 'clsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../Pagination';
import Select, { type SelectOption } from '../Select';

interface TableFooterProps {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    pageSizeOptions: number[];
    onPageSizeChange: (newPageSize: number) => void;
    onPageChange: (page: number) => void;
    className?: string;
    variant?: 'default' | 'withoutPageSelector';
    resultsText?: string;
}

const TableFooter = ({
    currentPage,
    pageSize,
    totalCount,
    pageSizeOptions,
    onPageSizeChange,
    onPageChange,
    variant = 'default',
    className,
    resultsText,
}: TableFooterProps) => {
    const { t } = useTranslation('common');

    const handlePageSizeSelectChange = useCallback(
        (value: string | number | null) => {
            if (value !== null) {
                onPageSizeChange(Number(value));
            }
        },
        [onPageSizeChange]
    );

    const handlePaginationChange = useCallback(
        (page: number, newPageSize: number) => {
            onPageChange(page);
            if (newPageSize !== pageSize) {
                onPageSizeChange(newPageSize);
            }
        },
        [onPageChange, onPageSizeChange, pageSize]
    );

    const selectOptions: SelectOption[] = useMemo(
        () =>
            pageSizeOptions.map(option => ({
                value: option.toString(),
                label: option.toString(),
            })),
        [pageSizeOptions]
    );

    const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalCount);

    const showingText = (
        <span className="text-base leading-normal font-medium text-neutral-500">
            {t('common:pagination.showing')} {startItem} - {endItem} {t('common:pagination.of')} {totalCount}{' '}
            {resultsText ? resultsText : t('common:pagination.results')}
        </span>
    );

    const pageSizeSelector = (
        <div className="hidden items-center gap-x-5 text-base leading-normal font-medium text-neutral-500 sm:flex">
            <label htmlFor="pageSizeSelectFooter" className="whitespace-nowrap">
                {t('common:pagination.show')}:
            </label>
            <Select
                id="pageSizeSelectFooter"
                name="pageSizeSelectFooter"
                options={selectOptions}
                value={pageSize.toString()}
                onChange={handlePageSizeSelectChange}
                className="min-w-[65px] text-base font-medium [&_[data-selected]]:font-medium"
            />
            <span className="whitespace-nowrap">{t('common:pagination.perPage', 'per page')}</span>
        </div>
    );

    const paginationControls =
        totalCount > 0 && totalCount > pageSize ? (
            <Pagination
                current={currentPage}
                total={totalCount}
                pageSize={pageSize}
                onChange={handlePaginationChange}
                className="w-full sm:w-auto"
            />
        ) : null;

    return (
        <div className={clsx('px-4 pt-2 sm:px-0', className)} data-cy="table-footer">
            {variant === 'withoutPageSelector' ? (
                <div className="grid grid-cols-1 gap-4 sm:flex sm:items-center sm:justify-between">
                    <div className="flex justify-center text-sm sm:justify-start">{showingText}</div>
                    {paginationControls && (
                        <div className="order-first flex justify-center sm:order-none">{paginationControls}</div>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:flex sm:items-center sm:justify-between">
                    <div className="flex justify-center text-sm sm:justify-start">{showingText}</div>
                    <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-end">
                        {paginationControls && (
                            <div className="order-first flex w-full justify-center sm:order-none sm:w-auto">
                                {paginationControls}
                            </div>
                        )}
                        <div className="flex justify-center">{pageSizeSelector}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableFooter;
